import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatDistanceToNowStrict } from 'date-fns'
import React from 'react'

import NotificationFacts from './NotificationFacts'

import type { NotificationFact } from './useNotificationFacts'

type NotificationSummaryProps = {
  /** The badge disc: a severity badge for a real notification, or any disc for a state that has none. */
  badge: React.ReactNode
  title: string
  description?: string
  /** ISO timestamp. Rendered as the relative line, so every surface words and formats it the same. */
  occurredAt?: string
  // Which one this is about — route, scope, day.
  facts?: NotificationFact[]
  // Sits with the facts rather than after the time.
  status?: React.ReactNode
}

/**
 * How a notification reads, wherever it is read: badge, title, subtitle, relative time. One component
 * rather than the same stack per surface — the popover row and the two toasts drifted on variants, text
 * colour and alignment precisely because each owned a copy.
 */
const NotificationSummary = ({ badge, title, description, occurredAt, facts = [], status }: NotificationSummaryProps) => (
  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
    {badge}
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="h6">{title}</Typography>
      {description && (
        <Typography color="text.primary" variant="body1">
          {description}
        </Typography>
      )}
      {(facts.length > 0 || status) && (
        <Stack useFlexGap direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}>
          <NotificationFacts facts={facts} />
          {status}
        </Stack>
      )}
      {occurredAt && (
        <Typography color="text.lightBrown" variant="caption">
          {formatDistanceToNowStrict(new Date(occurredAt), { addSuffix: true })}
        </Typography>
      )}
    </Stack>
  </Stack>
)

export default NotificationSummary
